import { ApprovalState } from '@mg/shared';
import type { CalendarItem } from '@mg/shared';
import { cn } from '../../lib/cn';
import { PlatformIcon } from './PlatformIcon';
import { PostThumbnail } from './PostThumbnail';

/** Local time label for a calendar item, e.g. "9:25 AM". */
export function calendarTimeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * A scheduled/published post rendered as a mini-card: platform icon · local time · title · media
 * thumbnail. Used in the month calendar day cells and the composer's per-day list. Clicking opens
 * the post in the composer.
 */
export function CalendarPostCard({ item, onOpen }: { item: CalendarItem; onOpen: () => void }) {
  const published = item.approvalState === ApprovalState.PUBLISHED;
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`${item.title} · ${calendarTimeLabel(item.scheduledAt)}`}
      className={cn(
        'focus-ring flex w-full items-center gap-1.5 rounded-md border p-1 text-left transition hover:shadow-card',
        published ? 'border-success/20 dark:border-success-dark/30 bg-success-tint dark:bg-success-dark-tint' : 'border-line bg-card',
      )}
    >
      <PlatformIcon platform={item.platform} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="text-[0.6875rem] font-semibold text-ink-strong">
            {calendarTimeLabel(item.scheduledAt)}
          </span>
          {published && <span className="text-[0.625rem] font-medium text-success dark:text-success-dark">· Posted</span>}
        </span>
        <span className="block truncate text-[0.6875rem] text-ink-muted">{item.title}</span>
      </span>
      <PostThumbnail mediaKind={item.mediaKind} thumbnailUrl={item.thumbnailUrl} className="h-8 w-8" />
    </button>
  );
}
